import jwt from "jwt-decode";
import React, {
  Dispatch,
  PropsWithChildren,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSetTimeZone } from "../Timezone";
import { AppStateStatus } from "react-native";
import { DateTime } from "luxon";
import { UnknownAction } from "redux";

export type SupportAccessInfo = {
  sessionId: string;
  expiresAt: number;
  orgId?: string;
  storeId?: string;
  adminEmail: string;
};

export type AuthSessionState = {
  authToken: string;
  refreshToken?: string;
  expiresAt?: number;
  idToken: string;
  user: User;
  isSharedDevice?: boolean;
  supportAccess?: SupportAccessInfo;

  lastActiveEmployees: string[];
  deviceClosedAt?: string;
  currentEmployeeId?: string | null;
  currentStoreId?: string;
  currentTeamId?: string;
  currentDashboardId?: string;
  currentWorkspaceId?: string;
  employeeSelectState?: "modal" | "toast" | "none";
};

type AuthState = {
  authSessions: AuthSessionState[];
  currentAuthSessionIndex?: number;

  lastDeviceAppState?: AppStateStatus;
};

type User = {
  exp: number;
  name: string;
  picture?: string;
  sub: string;
  email?: string;
};

const initalState: AuthState = {
  authSessions: [],
};

export enum AuthActions {
  AuthSuccess = "AuthSuccess",
  RefreshAuthSuccess = "RefreshAuthSuccess",
  SetCurrentAuthSessionIndex = "SetCurrentAuthSessionIndex",

  FailedToRefreshToken = "FailedToRefreshToken",
  FailedNoRefreshToken = "FailedNoRefreshToken",
  LogoutUser = "LogoutUser",
  FinishedSetup = "FinishedSetup",
  SetCurrentStoreId = "SetCurrentStoreId",
  SetCurrentTeamId = "SetCurrentTeamId",
  SetCurrentDashboardId = "SetCurrentDashboardId",
  SetCurrentWorkspaceId = "SetCurrentWorkspaceId",
  SetCurrentEmployeeId = "SetCurrentEmployeeId",
  DeviceStatusChange = "DeviceStatusChange",
  DeviceSelectStateDismiss = "DeviceSelectStateDismiss",
  DeviceSelectStateOpenModal = "DeviceSelectStateOpenModal",
  SupportAccessSuccess = "SupportAccessSuccess",
  SupportAccessEnd = "SupportAccessEnd",
}

export const currentAuthSession = (state: {
  auth: AuthState;
}): AuthSessionState | undefined => {
  if (
    state.auth.currentAuthSessionIndex !== undefined &&
    state.auth.authSessions[state.auth.currentAuthSessionIndex]
  ) {
    return state.auth.authSessions[state.auth.currentAuthSessionIndex];
  } else {
    return undefined;
  }
};

export const setCurrentAuthSessionIndex = (newCurrentUserIndex: number) =>
  ({
    type: AuthActions.SetCurrentAuthSessionIndex,
    payload: newCurrentUserIndex,
  }) as const;

export const logoutUser = (authSessionIndex: number) =>
  ({
    type: AuthActions.LogoutUser,
    payload: {
      authSessionIndex,
    },
  }) as const;

export const authSuccess = (
  authToken: string,
  idToken: string,
  refreshToken: string | null,
  expiresAt: number | null,
  isSharedDevice?: boolean
) =>
  ({
    type: AuthActions.AuthSuccess,
    payload: {
      authToken,
      refreshToken,
      idToken,
      expiresAt,
      isSharedDevice,
    },
  }) as const;

export const refreshAuthSuccess = (props: {
  oldRefreshToken: string;
  authToken: string;
  idToken: string;
  refreshToken: string | null;
  expiresAt: number | null;
}) =>
  ({
    type: AuthActions.RefreshAuthSuccess,
    payload: props,
  }) as const;

export const authFailedToRefreshToken = (refreshToken: string) =>
  ({
    type: AuthActions.FailedToRefreshToken,
    payload: {
      refreshToken,
    },
  }) as const;

export const authFailedNoRefreshToken = () =>
  ({
    type: AuthActions.FailedNoRefreshToken,
  }) as const;

export const supportAccessSuccess = (payload: {
  accessToken: string;
  sessionId: string;
  expiresAt: number;
  orgId?: string;
  storeId?: string;
  adminEmail: string;
  adminName: string;
}) =>
  ({
    type: AuthActions.SupportAccessSuccess,
    payload,
  }) as const;

export const supportAccessEnd = () =>
  ({
    type: AuthActions.SupportAccessEnd,
  }) as const;

export const authSetCurrentStoreId = (id: string) =>
  ({
    type: AuthActions.SetCurrentStoreId,
    payload: id,
  }) as const;

const authSetCurrentTeamId = (id: string | undefined) =>
  ({
    type: AuthActions.SetCurrentTeamId,
    payload: id,
  }) as const;

const authSetCurrentDashboardId = (id: string | undefined) =>
  ({
    type: AuthActions.SetCurrentDashboardId,
    payload: id,
  }) as const;

const authSetCurrentWorkspaceId = (id: string | undefined) =>
  ({
    type: AuthActions.SetCurrentWorkspaceId,
    payload: id,
  }) as const;

export const authSetCurrentEmployeeId = (id: string | undefined | null) =>
  ({
    type: AuthActions.SetCurrentEmployeeId,
    payload: id,
  }) as const;

export const authSetDeviceStatusChange = (
  date: string,
  status: AppStateStatus
) =>
  ({
    type: AuthActions.DeviceStatusChange,
    payload: {
      date,
      status,
    },
  }) as const;

export const authSetDeviceSelectStateDismiss = () =>
  ({
    type: AuthActions.DeviceSelectStateDismiss,
  }) as const;

export const authSetDeviceSelectStateOpenModal = () =>
  ({
    type: AuthActions.DeviceSelectStateOpenModal,
  }) as const;

const thresholdsInSec = {
  no_employee_modal: 60,
  toast: 10,
  modal: 60 * 5,
  modalAndClear: 60 * 30,
};
type Actions =
  | ReturnType<typeof authSuccess>
  | ReturnType<typeof refreshAuthSuccess>
  | ReturnType<typeof setCurrentAuthSessionIndex>
  | ReturnType<typeof authFailedToRefreshToken>
  | ReturnType<typeof authFailedNoRefreshToken>
  | ReturnType<typeof logoutUser>
  | ReturnType<typeof authSetCurrentStoreId>
  | ReturnType<typeof authSetCurrentTeamId>
  | ReturnType<typeof authSetCurrentDashboardId>
  | ReturnType<typeof authSetCurrentWorkspaceId>
  | ReturnType<typeof authSetCurrentEmployeeId>
  | ReturnType<typeof authSetDeviceStatusChange>
  | ReturnType<typeof authSetDeviceSelectStateDismiss>
  | ReturnType<typeof authSetDeviceSelectStateOpenModal>
  | ReturnType<typeof supportAccessSuccess>
  | ReturnType<typeof supportAccessEnd>;

export const authReducer = (
  state: AuthState = initalState,
  _action: UnknownAction
): AuthState => {
  const action = _action as Actions;

  switch (action.type) {
    case AuthActions.DeviceSelectStateDismiss: {
      return {
        ...state,
        authSessions: state.authSessions.map((authSession) => ({
          ...authSession,
          employeeSelectState: "none",
        })),
      };
    }
    case AuthActions.DeviceSelectStateOpenModal: {
      return {
        ...state,
        authSessions: state.authSessions.map((authSession) => ({
          ...authSession,
          employeeSelectState: "modal",
        })),
      };
    }
    case AuthActions.DeviceStatusChange: {
      const now = DateTime.fromISO(action.payload.date);
      if (action.payload.status === state.lastDeviceAppState) {
        return state;
      }
      return {
        ...state,
        lastDeviceAppState: action.payload.status,
        authSessions: state.authSessions.map((authSession, index) => {
          if (state.currentAuthSessionIndex === index) {
            switch (action.payload.status) {
              case "background":
              case "inactive":
              case "unknown":
              case "extension":
                return {
                  ...authSession,
                  deviceClosedAt: action.payload.date,
                };
              case "active": {
                const closedInSeconds = authSession.deviceClosedAt
                  ? now
                      .diff(DateTime.fromISO(authSession.deviceClosedAt))
                      .as("seconds")
                  : undefined;

                const newCurrentEmployeeId =
                  (closedInSeconds ?? 0) >= thresholdsInSec.modalAndClear
                    ? undefined
                    : authSession.currentEmployeeId;

                const newEmployeeSelectState = (() => {
                  if (authSession.employeeSelectState === "modal") {
                    return "modal";
                  }
                  if (closedInSeconds === undefined) {
                    return "modal";
                  }
                  if (!newCurrentEmployeeId) {
                    return closedInSeconds >= thresholdsInSec.no_employee_modal
                      ? "modal"
                      : "none";
                  }

                  if (closedInSeconds >= thresholdsInSec.modal) {
                    return "modal";
                  }
                  if (closedInSeconds >= thresholdsInSec.toast) {
                    return "toast";
                  }

                  return "none";
                })();

                return {
                  ...authSession,
                  currentEmployeeId: newCurrentEmployeeId,
                  deviceClosedAt: undefined,
                  employeeSelectState: newEmployeeSelectState,
                };
              }
              default:
                return authSession;
            }
          }
          return authSession;
        }),
      };
    }
    case AuthActions.SetCurrentEmployeeId: {
      return {
        ...state,
        authSessions: state.authSessions.map((authSession, index) => {
          if (state.currentAuthSessionIndex === index) {
            return {
              ...authSession,
              currentEmployeeId: action.payload,
              employeeSelectState: "none",
              lastActiveEmployees: action.payload
                ? [
                    action.payload,
                    ...authSession.lastActiveEmployees.filter(
                      (employee) => employee !== action.payload
                    ),
                  ].slice(0, 4)
                : authSession.lastActiveEmployees,
            };
          }
          return authSession;
        }),
      };
    }
    case AuthActions.SetCurrentWorkspaceId: {
      return {
        ...state,
        authSessions: state.authSessions.map((authSession, index) => {
          if (state.currentAuthSessionIndex === index) {
            return {
              ...authSession,
              currentWorkspaceId: action.payload,
            };
          }
          return authSession;
        }),
      };
    }
    case AuthActions.SetCurrentDashboardId: {
      return {
        ...state,
        authSessions: state.authSessions.map((authSession, index) => {
          if (state.currentAuthSessionIndex === index) {
            return {
              ...authSession,
              currentDashboardId: action.payload,
            };
          }
          return authSession;
        }),
      };
    }

    case AuthActions.SetCurrentTeamId: {
      return {
        ...state,
        authSessions: state.authSessions.map((authSession, index) => {
          if (state.currentAuthSessionIndex === index) {
            return {
              ...authSession,
              currentTeamId: action.payload,
            };
          }
          return authSession;
        }),
      };
    }

    case AuthActions.RefreshAuthSuccess: {
      return {
        ...state,
        authSessions: state.authSessions.map((authSession) => {
          if (authSession.refreshToken === action.payload.oldRefreshToken) {
            return {
              ...authSession,
              expiresAt: action.payload.expiresAt ?? undefined,
              authToken: action.payload.authToken,
              refreshToken:
                action.payload.refreshToken ?? authSession.refreshToken,
              idToken: action.payload.idToken,
            };
          }
          return authSession;
        }),
      };
    }

    case AuthActions.AuthSuccess: {
      const user: any = jwt(action.payload.idToken);

      const prevLoggedIn = state.authSessions.find(
        (session) => session.user.sub === user.sub
      );
      return {
        ...state,
        authSessions: [
          {
            ...prevLoggedIn,
            expiresAt: action.payload.expiresAt ?? undefined,
            authToken: action.payload.authToken,
            refreshToken: action.payload.refreshToken ?? undefined,
            idToken: action.payload.idToken,
            user,
            currentDashboardId: prevLoggedIn?.currentDashboardId,
            currentStoreId: prevLoggedIn?.currentStoreId,
            currentTeamId: prevLoggedIn?.currentTeamId,
            currentWorkspaceId: prevLoggedIn?.currentWorkspaceId,
            currentEmployeeId: prevLoggedIn?.currentEmployeeId,
            lastActiveEmployees: prevLoggedIn?.lastActiveEmployees ?? [],
            isSharedDevice: action.payload.isSharedDevice,
            employeeSelectState:
              (prevLoggedIn?.employeeSelectState ??
              action.payload.isSharedDevice)
                ? "modal"
                : "none",
          },
          ...state.authSessions.filter(
            (session) => user.sub !== session.user.sub
          ),
        ],
        currentAuthSessionIndex: 0,
      };
    }

    case AuthActions.SupportAccessSuccess: {
      const user: User = {
        sub: action.payload.sessionId,
        name: action.payload.adminName,
        email: action.payload.adminEmail,
        exp: action.payload.expiresAt,
      };
      return {
        ...state,
        authSessions: [
          {
            authToken: action.payload.accessToken,
            idToken: "",
            refreshToken: undefined,
            user,
            lastActiveEmployees: [],
            supportAccess: {
              sessionId: action.payload.sessionId,
              expiresAt: action.payload.expiresAt,
              orgId: action.payload.orgId,
              storeId: action.payload.storeId,
              adminEmail: action.payload.adminEmail,
            },
          },
        ],
        currentAuthSessionIndex: 0,
      };
    }

    case AuthActions.SupportAccessEnd: {
      return {
        ...state,
        authSessions: state.authSessions.filter(
          (session) => !session.supportAccess
        ),
        currentAuthSessionIndex: state.authSessions.some(
          (s) => !s.supportAccess
        )
          ? 0
          : undefined,
      };
    }

    case AuthActions.LogoutUser:
      return {
        ...state,
        currentAuthSessionIndex: undefined,
        authSessions: state.authSessions.filter(
          (_, index) => index !== action.payload.authSessionIndex
        ),
      };
    case AuthActions.FailedNoRefreshToken:
      const currentSessionIndex = state.currentAuthSessionIndex ?? 0;
      return {
        ...state,
        currentAuthSessionIndex: undefined,
        authSessions: state.authSessions.filter(
          (_, index) => index !== currentSessionIndex
        ),
      };

    case AuthActions.SetCurrentAuthSessionIndex:
      return {
        ...state,
        currentAuthSessionIndex: action.payload,
      };

    case AuthActions.FailedToRefreshToken:
      const failedSessionIndex = state.authSessions.findIndex(
        (session) => session.refreshToken === action.payload.refreshToken
      );
      return {
        ...state,
        currentAuthSessionIndex:
          state.currentAuthSessionIndex === failedSessionIndex
            ? undefined
            : state.currentAuthSessionIndex,
        authSessions: state.authSessions.filter(
          (session) => session.refreshToken !== action.payload.refreshToken
        ),
      };
    case AuthActions.SetCurrentStoreId:
      return {
        ...state,
        authSessions: state.authSessions.map((session, index) =>
          index === state.currentAuthSessionIndex
            ? {
                ...session,
                currentStoreId: action.payload,
                currentTeamId: undefined,
              }
            : session
        ),
      };
  }
  return state;
};

const ProviderPair = React.createContext<{
  dispatch: Dispatch<Actions>;
  state: AuthState;
}>({} as any);

const auth = (state: any): AuthState => state.auth;

export const useAuthState = () => [useSelector(auth), useDispatch()] as const;

export const useUser = () => {
  const [state] = useAuthState();
  if (state.currentAuthSessionIndex !== undefined) {
    return state.authSessions[state.currentAuthSessionIndex];
  }
  throw new Error("NOT LOGGED IN");
};

export const useOptionalUser = () => {
  const [state] = useAuthState();
  if (state.currentAuthSessionIndex !== undefined) {
    return state.authSessions[state.currentAuthSessionIndex];
  } else {
    return null;
  }
};

export function usePersistedTeam() {
  const [, dispatch] = useAuthState();
  const usr = useOptionalUser();

  const st = useCallback(
    (id: string | undefined) => dispatch(authSetCurrentTeamId(id)),
    [dispatch]
  );

  const f = useMemo(
    () => ({
      currentTeamId: usr?.currentTeamId,
      setCurrentTeamId: st,
    }),
    [st, usr?.currentTeamId]
  );
  return f;
}

export function usePersistedDashboardId() {
  const [, dispatch] = useAuthState();
  const usr = useOptionalUser();

  const st = useCallback(
    (id: string | undefined) => dispatch(authSetCurrentDashboardId(id)),
    [dispatch]
  );

  const f = useMemo(
    () => ({
      currentDashboardId: usr?.currentDashboardId,
      setCurrentDashboard: st,
    }),
    [st, usr?.currentDashboardId]
  );
  return f;
}

export function usePersistedWorkspaceId() {
  const [, dispatch] = useAuthState();
  const usr = useOptionalUser();

  const st = useCallback(
    (id: string | undefined) => dispatch(authSetCurrentWorkspaceId(id)),
    [dispatch]
  );

  const f = useMemo(
    () => ({
      currentWorkspaceId: usr?.currentWorkspaceId,
      setCurrentWorkspaceId: st,
    }),
    [st, usr?.currentWorkspaceId]
  );
  return f;
}
export const LoginProvider = ({ children }: PropsWithChildren<object>) => {
  const [state, dispatch] = useAuthState();

  return (
    <ProviderPair.Provider value={{ state, dispatch }}>
      {children}
    </ProviderPair.Provider>
  );
};

export function useSetCurrentStoreId() {
  const [, dispatch] = useAuthState();

  const setTz = useSetTimeZone();

  return useCallback(
    (id: string, timezone: string) => {
      setTz(timezone);
      dispatch(authSetCurrentStoreId(id));
    },
    [dispatch, setTz]
  );
}

export function useLastActiveEmployees() {
  const usr = useOptionalUser();
  return useMemo(() => {
    return usr?.lastActiveEmployees ?? [];
  }, [usr?.lastActiveEmployees]);
}
